import { computed } from "vue";
import { useStore } from "vuex";
import { alphanumericSort } from "@/util/alphanumericSort.ts";
import { parseDate } from "@/util/date.ts";

export default function useSortEntriesCompareFn() {
  const store = useStore();

  const defaultSorting = computed(() =>
    store.getters.getStoreVar(
      "Category",
      "page",
      "dropdown",
      "orderingAttribute"
    )
  );

  const sortDown = computed(() =>
    store.getters.getStoreVar("Category", "page", "toggle", "sortDescending")
  );

  const valueGoingUp = computed(() => (sortDown.value ? -1 : 1));
  const valueGoingDown = computed(() => -valueGoingUp.value);

  const isNewProfile = (person) => person.firstName === "NEW PROFILE";

  return computed(() => {
    if (defaultSorting.value === "custom") {
      return (a, b) => (a.index >= b.index ? 1 : -1);
    }
    if (["type", "activity"].includes(defaultSorting.value)) {
      return (a, b) => {
        if (isNewProfile(a)) return -1;
        if (isNewProfile(b)) return 1;

        const valA = a[defaultSorting.value]?.content || "";
        const valB = b[defaultSorting.value]?.content || "";

        return alphanumericSort(valA, valB, sortDown.value);
      };
    }
    if (["eventDate", "graduationDate"].includes(defaultSorting.value)) {
      return (a, b) => {
        // new profiles should always appear first
        if (isNewProfile(a)) return -1;
        if (isNewProfile(b)) return 1;

        const dateA = a[defaultSorting.value] || "0000-00-00";
        const dateB = b[defaultSorting.value] || "0000-00-00";

        const { year: yearA, month: monthA, day: dayA } = parseDate(dateA);
        const { year: yearB, month: monthB, day: dayB } = parseDate(dateB);

        if (yearA !== yearB) {
          if (yearA > yearB) return valueGoingUp.value;
          if (yearA < yearB) return valueGoingDown.value;
        }
        if (monthA !== monthB) {
          if (monthA > monthB) return valueGoingUp.value;
          if (monthA < monthB) return valueGoingDown.value;
        }
        if (dayA !== dayB) {
          if (dayA > dayB) return valueGoingUp.value;
          if (dayA < dayB) return valueGoingDown.value;
        }
        if (dayA !== dayB) return (dayA - dayB) * valueGoingUp.value;
        return 0;
      };
    }
    return (a, b) => {
      if (isNewProfile(a)) return -1;
      if (isNewProfile(b)) return 1;

      const personA = (a[defaultSorting.value] || "").toUpperCase().trim();
      const personB = (b[defaultSorting.value] || "").toUpperCase().trim();
      if (personA > personB) return valueGoingUp.value;
      if (personA < personB) return valueGoingDown.value;
      return 0;
    };
  });
}
